import { Clock, MessageSquare, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export interface QuickActionBarProps {
  /** 点击工时记录按钮 */
  onTimelogClick: () => void
  /** 点击评论按钮 */
  onCommentClick: () => void
  /** 是否显示 */
  visible?: boolean
  /** 自定义类名 */
  className?: string
}

/**
 * 快捷操作栏组件
 * 
 * 在任务行悬停时显示快捷操作按钮，支持：
 * - 快速记录工时
 * - 快速添加评论
 * - 更多操作菜单
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4
 */
export function QuickActionBar({
  onTimelogClick,
  onCommentClick,
  visible = true,
  className,
}: QuickActionBarProps) {
  if (!visible) {return null}

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100',
        className
      )}
    >
      {/* 工时记录按钮 (Requirement 3.2) */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={(e) => {
          e.stopPropagation()
          onTimelogClick()
        }}
        title="记录工时"
      >
        <Clock className="h-4 w-4" />
        <span className="sr-only">记录工时</span>
      </Button>

      {/* 评论按钮 (Requirement 3.3) */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        onClick={(e) => {
          e.stopPropagation()
          onCommentClick()
        }}
        title="添加评论"
      >
        <MessageSquare className="h-4 w-4" />
        <span className="sr-only">添加评论</span>
      </Button>

      {/* 更多操作菜单 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">更多操作</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onTimelogClick}>
            <Clock className="mr-2 h-4 w-4" />
            记录工时
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCommentClick}>
            <MessageSquare className="mr-2 h-4 w-4" />
            添加评论
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

/**
 * 快捷操作栏容器
 * 用于包裹表格行，提供 hover 状态
 */
export interface QuickActionRowProps {
  children: React.ReactNode
  className?: string
}

export function QuickActionRow({ children, className }: QuickActionRowProps) {
  return (
    <div className={cn('group relative', className)}>
      {children}
    </div>
  )
}
